import { useState } from "react";
import { motion } from "framer-motion";
import { X, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { collections, colors, brands } from "@/data/products";

export interface FilterState {
  collections: string[];
  colors: string[];
  brands: string[];
  priceRange: [number, number];
}

interface ProductFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  productCount: number;
}

const FilterGroup = ({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 text-sm font-semibold text-foreground hover:text-primary transition-colors">
        {title}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const FiltersContent = ({
  filters,
  onFiltersChange,
}: {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}) => {
  const toggleCollection = (collection: string) => {
    const newCollections = filters.collections.includes(collection)
      ? filters.collections.filter((c) => c !== collection)
      : [...filters.collections, collection];
    onFiltersChange({ ...filters, collections: newCollections });
  };

  const toggleColor = (color: string) => {
    const newColors = filters.colors.includes(color)
      ? filters.colors.filter((c) => c !== color)
      : [...filters.colors, color];
    onFiltersChange({ ...filters, colors: newColors });
  };

  const toggleBrand = (brand: string) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter((b) => b !== brand)
      : [...filters.brands, brand];
    onFiltersChange({ ...filters, brands: newBrands });
  };

  const clearFilters = () => {
    onFiltersChange({
      collections: [],
      colors: [],
      brands: [],
      priceRange: [0, 20],
    });
  };

  const hasActiveFilters =
    filters.collections.length > 0 ||
    filters.colors.length > 0 ||
    filters.brands.length > 0 ||
    filters.priceRange[0] > 0 ||
    filters.priceRange[1] < 20;

  return (
    <div className="space-y-1">
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="w-full justify-start text-primary hover:text-primary mb-4"
        >
          <X className="w-4 h-4 mr-2" />
          Clear All Filters
        </Button>
      )}

      {/* Collections */}
      <FilterGroup title="Collections">
        <div className="space-y-3">
          {collections.map((collection) => (
            <label
              key={collection.id}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <Checkbox
                checked={filters.collections.includes(collection.name)}
                onCheckedChange={() => toggleCollection(collection.name)}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {collection.name}
              </span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* Colors */}
      <FilterGroup title="Colors">
        <div className="space-y-3">
          {colors.map((color) => (
            <label
              key={color}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <Checkbox
                checked={filters.colors.includes(color)}
                onCheckedChange={() => toggleColor(color)}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {color}
              </span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* Brands */}
      <FilterGroup title="Brands">
        <div className="space-y-3">
          {brands.map((brand) => (
            <label
              key={brand}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <Checkbox
                checked={filters.brands.includes(brand)}
                onCheckedChange={() => toggleBrand(brand)}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {brand}
              </span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* Price Range */}
      <FilterGroup title="Price Range">
        <div className="px-2 pt-2">
          <Slider
            value={filters.priceRange}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                priceRange: value as [number, number],
              })
            }
            max={20}
            min={0}
            step={1}
            className="mb-4"
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>${filters.priceRange[0]}/sqft</span>
            <span>${filters.priceRange[1]}+/sqft</span>
          </div>
        </div>
      </FilterGroup>
    </div>
  );
};

const ProductFilters = ({
  filters,
  onFiltersChange,
  productCount,
}: ProductFiltersProps) => {
  // Active filter badges
  const activeFilters = [
    ...filters.collections,
    ...filters.colors,
    ...filters.brands,
  ];

  return (
    <>
      {/* Desktop Filters */}
      <div className="hidden lg:block">
        <div className="sticky top-24 bg-background rounded-xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Filters</h3>
          <FiltersContent filters={filters} onFiltersChange={onFiltersChange} />
        </div>
      </div>

      {/* Mobile Filters */}
      <div className="lg:hidden mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[350px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FiltersContent
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                />
              </div>
            </SheetContent>
          </Sheet>

          <span className="text-sm text-muted-foreground">
            {productCount} products
          </span>

          {/* Active filter chips */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter) => (
                <Badge
                  key={filter}
                  variant="secondary"
                  className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => {
                    // Remove the filter
                    if (filters.collections.includes(filter)) {
                      onFiltersChange({
                        ...filters,
                        collections: filters.collections.filter(
                          (c) => c !== filter
                        ),
                      });
                    } else if (filters.colors.includes(filter)) {
                      onFiltersChange({
                        ...filters,
                        colors: filters.colors.filter((c) => c !== filter),
                      });
                    } else if (filters.brands.includes(filter)) {
                      onFiltersChange({
                        ...filters,
                        brands: filters.brands.filter((b) => b !== filter),
                      });
                    }
                  }}
                >
                  {filter}
                  <X className="w-3 h-3" />
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductFilters;
